import { useT } from "@transifex/react";
import { useState } from "react";
import Checkbox from "../../../basic/checkbox/Checkbox";
import RadioOption from "../../../basic/RadioOption";
import BasePopupWrapper, { TitleColorVariants } from "../../../basic/popups/BasePopupWrapper";
import { MergeConflictStrategy } from "../../../helpers/ClassMemberMerge";
import useBreakpoints from "../../../hooks/useBreakpoints";
import type { ClassMember } from "../../../networking/schoolClasses";
import ClassMemberJoinedLabel from "../classDetails/ClassMemberJoinedLabel";
import NewClassMemberBadge from "../classDetails/NewClassMemberBadge";
import { toggleInSet } from "../../../helpers/SetHelper";

interface ReviewDuplicatesPopupProps {
    isOpen: boolean;
    group: ClassMember[];
    newMemberIds: Set<string>;
    onClose: () => void;
    /** Absent: the secondary button is a plain Cancel instead of "Keep separate". */
    onKeepSeparate?: () => void;
    hasMergeConflicts: (selected: ClassMember[]) => Promise<boolean>;
    onMerge: (selected: ClassMember[], strategy: MergeConflictStrategy) => Promise<void>;
}

// The popup wrapper stretches buttons to full width below SM.
const BUTTON_CLASS = "min-w-[200px]";

enum Step {
    SELECT,
    CONFLICT,
}

const ReviewDuplicatesPopup = (props: ReviewDuplicatesPopupProps) => {
    const t = useT();
    const { isAboveMD: isDesktop } = useBreakpoints();

    const [step, setStep] = useState(Step.SELECT);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(props.group.map((m) => m.id)));
    const [strategy, setStrategy] = useState(MergeConflictStrategy.FIRST);
    const [isBusy, setIsBusy] = useState(false);

    const t_title = t("Review duplicates", { _tags: "SchoolClasses, ReviewDuplicatesPopup" });
    const t_question = t("Should the following entries be merged?", {
        _tags: "SchoolClasses, ReviewDuplicatesPopup",
    });
    const t_note = t("Submissions and evaluations are kept.", { _tags: "SchoolClasses, ReviewDuplicatesPopup" });
    const t_keepSeparate = t("Keep separately", { _tags: "SchoolClasses, ReviewDuplicatesPopup" });
    const t_mergeSelection = t("Merge selection", { _tags: "SchoolClasses, ReviewDuplicatesPopup" });
    const t_conflictQuestion = t("Some submissions exist more than once. What should happen to them?", {
        _tags: "SchoolClasses, ReviewDuplicatesPopup",
    });
    const t_conflictNote = t("The other submissions of these entries are removed.", {
        _tags: "SchoolClasses, ReviewDuplicatesPopup",
    });
    const t_first = t("The first submission counts.", { _tags: "SchoolClasses, ReviewDuplicatesPopup" });
    const t_last = t("The last submission counts.", { _tags: "SchoolClasses, ReviewDuplicatesPopup" });
    const t_cancel = t("Cancel", { _tags: "SchoolClasses, ReviewDuplicatesPopup" });
    const t_merge = t("Merge", { _tags: "SchoolClasses, ReviewDuplicatesPopup" });

    const selected = props.group.filter((member) => selectedIds.has(member.id));

    const toggle = (id: string) => setSelectedIds((prev) => toggleInSet(prev, id));

    // Errors are surfaced by the parent; here they only end the busy state.
    const runBusy = async (action: () => Promise<void>) => {
        setIsBusy(true);
        try {
            await action();
        } catch (e) {
            console.error(e);
        } finally {
            setIsBusy(false);
        }
    };

    const handleMergeSelection = () =>
        runBusy(async () => {
            const hasConflicts = await props.hasMergeConflicts(selected);
            if (hasConflicts) {
                setStep(Step.CONFLICT);
                return;
            }
            await props.onMerge(selected, MergeConflictStrategy.FIRST);
        });

    const handleMergeWithStrategy = () => runBusy(() => props.onMerge(selected, strategy));

    const handleClose = isBusy ? () => {} : props.onClose;

    const renderMemberRow = (member: ClassMember) => {
        const isChecked = selectedIds.has(member.id);

        return (
            <button
                key={member.id}
                type="button"
                role="checkbox"
                aria-checked={isChecked}
                data-testid={`duplicate-member-${member.id}`}
                onClick={() => toggle(member.id)}
                className={`flex gap-s px-xs py-s w-full bg-transparent border-none text-left cursor-pointer ${
                    isDesktop ? "items-center" : "items-start"
                }`}
            >
                <Checkbox isChecked={isChecked} />
                <span
                    className={`flex flex-1 min-w-0 gap-xs ${
                        isDesktop ? "flex-row items-center justify-between gap-s" : "flex-col"
                    }`}
                >
                    <span className="flex items-center gap-s min-w-0">
                        <span className="text-l text-p6-grey-dark [overflow-wrap:anywhere]">{member.name}</span>
                        {props.newMemberIds.has(member.id) && <NewClassMemberBadge />}
                    </span>
                    <ClassMemberJoinedLabel classMember={member} />
                </span>
            </button>
        );
    };

    const renderStrategyOption = (option: MergeConflictStrategy, label: string) => (
        <RadioOption
            name="merge-strategy"
            value={option}
            label={label}
            isChecked={strategy === option}
            onSelect={() => setStrategy(option)}
        />
    );

    if (step === Step.CONFLICT) {
        return (
            <BasePopupWrapper
                isOpen={props.isOpen}
                onClose={handleClose}
                title={t_title}
                titleColor={TitleColorVariants.DEFAULT}
                secondaryButton={{ text: t_cancel, onClick: props.onClose, disabled: isBusy, className: BUTTON_CLASS }}
                primaryButton={{
                    text: t_merge,
                    onClick: handleMergeWithStrategy,
                    disabled: isBusy,
                    className: BUTTON_CLASS,
                }}
            >
                <div className="flex flex-col gap-s">
                    <p className="text-l text-p6-grey-dark">{t_conflictQuestion}</p>
                    {renderStrategyOption(MergeConflictStrategy.FIRST, t_first)}
                    {renderStrategyOption(MergeConflictStrategy.LAST, t_last)}
                    <p className="text-m text-p6-grey-mid">{t_conflictNote}</p>
                </div>
            </BasePopupWrapper>
        );
    }

    return (
        <BasePopupWrapper
            isOpen={props.isOpen}
            onClose={handleClose}
            title={t_title}
            titleColor={TitleColorVariants.DEFAULT}
            secondaryButton={{
                text: props.onKeepSeparate ? t_keepSeparate : t_cancel,
                onClick: props.onKeepSeparate ?? props.onClose,
                disabled: isBusy,
                className: BUTTON_CLASS,
            }}
            primaryButton={{
                text: t_mergeSelection,
                onClick: handleMergeSelection,
                disabled: isBusy || selected.length < 2,
                className: BUTTON_CLASS,
            }}
        >
            <div className="flex flex-col gap-s">
                <p className="text-l text-p6-grey-dark">{t_question}</p>
                <div className="flex flex-col divide-y divide-p6-grey-light">{props.group.map(renderMemberRow)}</div>
                <p className="text-m text-p6-grey-mid">{t_note}</p>
            </div>
        </BasePopupWrapper>
    );
};

export default ReviewDuplicatesPopup;
